import { Check, Download, Phone } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { Section, SectionHeading } from "../components/Section";
import Reveal from "../components/Reveal";
import MailingList from "../components/MailingList";
import { LOCATIONS } from "../lib/content";

const WB = LOCATIONS["west-branch"];

/** Every string below is the marina's own published Boating Club copy, carried
 *  over verbatim. Nothing here is written, summarised or embellished by us.
 *  All assets are served from our own origin. */
const PACKET_URL = "/site-assets/club/2026-Boating-Club_update.pdf";
const BANNER = "/site-assets/club/boating-club-banner.png";

const WHAT_IS = [
  "It is a unique program that combines the principal ideas of leasing and time sharing to offer our customers an affordable, flexible, and worry-free approach to boating",
  "Our new program relieves much of the financial burden of owning a boat and frees you from the time associated with maintenance and cleaning, while lowering your costs at the same time.",
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Four Levels",
    icon: "/site-assets/club/tag-1-icon.svg",
    body: "Simply purchase one of the four levels. With each level you will receive a designated number of points that may be used during the 2024 boating season.",
  },
  {
    step: "02",
    title: "Season",
    icon: "/site-assets/club/schedule-1.svg",
    body: "The season begins April 1st and ends October 31st (weather permitting).",
  },
  {
    step: "03",
    title: "Apply Points",
    icon: "/site-assets/club/gift-1-icon.svg",
    body: "You apply those points according to the type of boat that you choose and the time of the season that you choose.",
  },
  {
    step: "04",
    title: "Advantage",
    icon: "/site-assets/club/like-1.svg",
    body: "One of the great advantages is that you may choose from a variety of sizes and styles. Mix and match any way you choose. It’s like owning many different boats all at once.",
  },
];

const PACKAGES = [
  { name: "Platinum Package", points: "43 Points", price: "$4000.00" },
  { name: "Gold Package", points: "32 Points", price: "$3000.00" },
  { name: "Silver Package", points: "21 Points", price: "$2000.00" },
  { name: "Bronze Package", points: "10 Points", price: "$1000.00" },
];

const BENEFITS = [
  "Flexible Scheduling",
  "Two Locations",
  "No Insurance Fees",
  "Three Levels Of Commitment",
  "Many Different Boats to Choose",
  "No Winterization/Winter Storage Fees",
  "No Maintenance Cost",
  "No Docking Fees",
  "No Trailering",
];

export default function BoatingClub() {
  return (
    <>
      <Seo
        title="Boating Club | West Branch Marina"
        description="The West Branch Marina and Lake Milton Marina Boating Club: all the benefits of owning a boat without all the expense."
        path="/boating-club"
        image={BANNER}
      />

      <PageHero
        image={BANNER}
        alt="West Branch Marina Boating Club"
        eyebrow="West Branch Marina/Lake Milton Marina"
        title="Boating Club"
        subtitle="All the benefits of owning a boat without all the expense!"
      >
        <a
          href={PACKET_URL}
          download
          data-testid="club-packet-hero-link"
          className="inline-flex items-center gap-3 rounded-full bg-ember px-7 py-4 font-bold text-white transition-all hover:bg-ember-hover hover:-translate-y-0.5"
        >
          <Download className="h-5 w-5" aria-hidden="true" />
          2026 Club Info/Sign Up Packet Click Here
        </a>
      </PageHero>

      <Section tone="white">
        <SectionHeading eyebrow="The programme" title="What is The Boating Club?" />
        <div className="mt-10 grid gap-8 lg:grid-cols-[1.25fr_1fr] lg:gap-14">
          <ul data-testid="club-what-is-list" className="space-y-4">
            {WHAT_IS.map((line, i) => (
              <Reveal
                key={i}
                delay={i * 80}
                data-testid={`club-what-is-${i + 1}`}
                className="flex gap-3 rounded-2xl border border-sand-border bg-sand p-5 text-sm leading-relaxed text-slate-700"
              >
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-lake" aria-hidden="true" />
                <span>{line}</span>
              </Reveal>
            ))}
          </ul>
          <Reveal className="rounded-2xl border border-sand-border bg-sand-card p-6">
            <p className="text-sm leading-relaxed text-slate-700">
              Love boating and being around the water, but thought you couldn’t afford the lifestyle?
            </p>
            <p className="mt-3 text-sm leading-relaxed text-slate-700">
              Thank again, we have developed a program just for you.
            </p>
            <ul data-testid="club-benefits-list" className="mt-6 flex flex-wrap gap-2">
              {BENEFITS.map((b) => (
                <li
                  key={b}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-navy"
                >
                  <Check className="h-3.5 w-3.5 shrink-0 text-lake" aria-hidden="true" />
                  {b}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="Four simple steps" title="How it works" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {HOW_IT_WORKS.map((s, i) => (
            <Reveal
              key={s.step}
              delay={i * 70}
              data-testid={`club-step-${s.step}`}
              className="rounded-2xl border border-sand-border bg-white p-6"
            >
              <span className="font-display text-3xl font-extrabold text-lake/40">{s.step}</span>
              <img src={s.icon} alt="" aria-hidden="true" className="mt-3 h-10 w-10" loading="lazy" />
              <h3 className="mt-3 font-display text-lg font-bold text-navy">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tone="white">
        <SectionHeading eyebrow="Membership levels" title="See our Packages" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PACKAGES.map((p, i) => (
            <Reveal
              key={p.name}
              delay={i * 70}
              data-testid={`club-package-${p.name.split(" ")[0].toLowerCase()}`}
              className="flex h-full flex-col rounded-3xl border border-sand-border bg-sand p-7"
            >
              <h3 className="font-display text-xl font-bold text-navy">{p.name}</h3>
              <p className="mt-2 text-sm font-semibold text-lake">{p.points}</p>
              <p className="mt-4 font-display text-3xl font-extrabold text-navy">{p.price}</p>
              <a
                href={PACKET_URL}
                download
                data-testid={`club-package-link-${p.name.split(" ")[0].toLowerCase()}`}
                className="mt-auto pt-7 text-sm font-bold text-ember hover:underline"
              >
                Click for More Info
              </a>
            </Reveal>
          ))}
        </div>
        <p data-testid="club-enquiry-note" className="mt-10 flex flex-wrap items-center gap-3 text-sm text-slate-600">
          <a
            href={PACKET_URL}
            download
            data-testid="club-packet-link"
            className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-bold text-white hover:bg-navy-surface"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            2026 Club Info/Sign Up Packet Click Here
          </a>
          <a
            href={WB.phoneHref}
            data-testid="club-call-link"
            className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-6 py-3 text-sm font-bold text-navy hover:bg-sand-card"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            {WB.phone}
          </a>
        </p>
      </Section>

      <MailingList
        testId="club-mailing-list"
        footnote="It's 100% free and we will never send more than one email per month"
      />
    </>
  );
}
